import { useState, useEffect } from "react";
import { shift, size, type Middleware } from "@floating-ui/react";

// Below this width anchored popovers can't fit beside their anchor, so hover
// cards render as bottom sheets instead (see RestaurantHoverCard/DraggableUser).
export const SMALL_SCREEN_QUERY = "(max-width: 640px)";

export function useIsSmallScreen() {
  const [isSmall, setIsSmall] = useState(
    () => typeof window !== "undefined" && window.matchMedia(SMALL_SCREEN_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(SMALL_SCREEN_QUERY);
    const onChange = () => setIsSmall(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isSmall;
}

/**
 * Keeps a floating card fully inside the viewport. shift()'s default main axis
 * for left/right placements is vertical only, so crossAxis is needed to correct
 * horizontal overflow; size() then caps the card to the space that remains, so
 * it can never exceed the viewport even when wider than the screen.
 */
export function viewportClamp(maxWidth: number): Middleware[] {
  return [
    shift({ padding: 8, crossAxis: true }),
    size({
      padding: 8,
      apply({ availableWidth, availableHeight, elements }) {
        Object.assign(elements.floating.style, {
          maxWidth: `${Math.min(maxWidth, availableWidth)}px`,
          maxHeight: `${Math.max(160, availableHeight)}px`,
        });
      },
    }),
  ];
}
